import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch } from '../../hooks/redux';
import { setCredentials } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

import CustomSocialMediaButton from '../CustomSocialMediaButton';
import GoogleIcon from '../../assets/icons/Google.png';
import { isInviteFlowEnabled } from '../../constants/inviteFlow';
import { clearReferralToken, getReferralToken } from '../../constants/referralToken';

import styles from './GoogleButton.module.scss';

declare global {
  interface Window {
    google: any;
  }
}

const GOOGLE_AUTH_LOG_PREFIX = '[Google Auth]';

function waitForGoogleIdentityServices(maxWaitMs = 15000): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const t = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        window.clearInterval(t);
        resolve();
      } else if (Date.now() - start > maxWaitMs) {
        window.clearInterval(t);
        reject(new Error('Google Identity Services script did not load in time'));
      }
    }, 50);
  });
}

const GoogleButton = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isInviteFlow = isInviteFlowEnabled();

  const handleCredentialResponse = useCallback(
    async (response: any) => {
      const googleToken = response.credential;

      try {
        const referralToken = getReferralToken();

        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/sign-in/google/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            google_token: googleToken,
            ...(referralToken ? { referral_token: referralToken } : {}),
          }),
        });

        if (!res.ok) throw new Error('Login failed');

        const data = (await res.json()) as {
          access: string;
          user?: { is_profile_complete?: boolean };
        };

        if (referralToken) {
          clearReferralToken();
        }

        dispatch(setCredentials({ ...data }));
        localStorage.setItem('authToken', data.access);

        let profileComplete = Boolean(data.user?.is_profile_complete);

        try {
          const meRes = await fetch(`${process.env.REACT_APP_API_URL}/users/me/`, {
            headers: {
              Authorization: `Bearer ${data.access}`,
              Accept: 'application/json',
            },
          });
          if (meRes.ok) {
            const me = (await meRes.json()) as { is_profile_complete?: boolean };
            if (typeof me.is_profile_complete === 'boolean') {
              profileComplete = me.is_profile_complete;
            }
          }
        } catch {
          /* fall back to Google sign-in payload */
        }

        navigate(profileComplete ? '/profile' : '/personalized-form');
      } catch (err) {
        console.error('Google login error:', err);
      }
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    if (isInviteFlow) {
      return;
    }

    let cancelled = false;
    const hostForCleanup = overlayRef.current;

    waitForGoogleIdentityServices()
      .then(() => {
        if (cancelled) return;

        const mount = () => {
          if (cancelled) return;
          const host = overlayRef.current;
          if (!host) return;

          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
            callback: handleCredentialResponse,
            ux_mode: 'popup',
            use_fedcm_for_button: false,
          });

          host.innerHTML = '';
          const measured = Math.round(wrapRef.current?.getBoundingClientRect().width ?? 0);
          const widthPx = Math.max(320, measured > 0 ? measured : 400);

          window.google.accounts.id.renderButton(host, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            shape: 'pill',
            logo_alignment: 'left',
            width: widthPx,
          });

          console.log(
            `${GOOGLE_AUTH_LOG_PREFIX} Google sign-in ready (button flow, non-FedCM; overlay receives real clicks).`,
          );
        };

        requestAnimationFrame(mount);
      })
      .catch(() => {
        if (!cancelled) {
          console.error(
            `${GOOGLE_AUTH_LOG_PREFIX} Failed to load Google script — check network and ad blockers.`,
          );
        }
      });

    return () => {
      cancelled = true;
      if (hostForCleanup) hostForCleanup.innerHTML = '';
      try {
        window.google?.accounts?.id?.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [handleCredentialResponse, isInviteFlow]);

  if (isInviteFlow) {
    return null;
  }

  return (
    <div
      ref={wrapRef}
      className={styles.googleButtonWrap}
      onClickCapture={() => {
        console.log(`${GOOGLE_AUTH_LOG_PREFIX} Continue with Google was clicked.`);
      }}
    >
      <CustomSocialMediaButton
        customStyle={styles.socialMediaButton}
        text="Continue with Google"
        icon={GoogleIcon}
        onClick={() => {
          /* primary interaction is the transparent GIS layer above */
        }}
      />
      <div
        ref={overlayRef}
        className={styles.googleButtonOverlay}
      />
    </div>
  );
};

export default GoogleButton;
